import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useNavigate } from "react-router-dom";
import {
  FiBell,
  FiCheck,
  FiFileText,
  FiRefreshCw,
  FiTool,
} from "react-icons/fi";
import {
  ComunicacaoCliente,
  marcarComunicacaoComoLida,
  obterFeedComunicacoesCliente,
} from "../../services/comunicacoesCliente";

const getNotificationIcon = (tipo: ComunicacaoCliente["tipo"]) => {
  if (tipo === "CartaMensal") return <FiFileText />;
  if (tipo === "InformeImpostoRenda") return <FiFileText />;
  if (tipo === "CotaManutencao") return <FiTool />;
  return <FiRefreshCw />;
};

const formatNotificationDate = (date: string) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });

export const NotificationBell = () => {
  const [comunicacoes, setComunicacoes] = useState<ComunicacaoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const loadNotifications = useCallback(async () => {
    try {
      const feed = await obterFeedComunicacoesCliente(10);
      setComunicacoes(feed.comunicacoes || []);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = window.setInterval(loadNotifications, 60000);
    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  const unreadCount = useMemo(
    () => comunicacoes.filter((comunicacao) => !comunicacao.lida).length,
    [comunicacoes]
  );

  const markAsRead = async (comunicacao: ComunicacaoCliente) => {
    if (!comunicacao.lida) {
      setComunicacoes((current) =>
        current.map((item) => item.id === comunicacao.id ? { ...item, lida: true } : item)
      );

      try {
        await marcarComunicacaoComoLida(comunicacao.id);
      } catch {
        loadNotifications();
      }
    }

    if (comunicacao.tipo === "CartaMensal") {
      navigate("/relatorios");
    } else if (comunicacao.tipo === "InformeImpostoRenda") {
      navigate("/extrato");
    }
  };

  const markAllAsRead = async () => {
    const unreadItems = comunicacoes.filter((comunicacao) => !comunicacao.lida);
    setComunicacoes((current) => current.map((item) => ({ ...item, lida: true })));

    try {
      await Promise.all(unreadItems.map((item) => marcarComunicacaoComoLida(item.id)));
    } catch {
      loadNotifications();
    }
  };

  return (
    <Dropdown className="omex-notifications" align="end" onToggle={(isOpen) => isOpen && loadNotifications()}>
      <Dropdown.Toggle
        as="button"
        type="button"
        className="omex-notifications-toggle"
        aria-label={unreadCount ? `${unreadCount} notificações não lidas` : "Notificações"}
        title="Notificações"
      >
        <FiBell aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="omex-notifications-count">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="omex-notifications-menu">
        <div className="omex-notifications-header">
          <div>
            <strong>Notificações</strong>
            <span>{unreadCount ? `${unreadCount} não lidas` : "Tudo em dia"}</span>
          </div>
          {unreadCount > 0 && (
            <button type="button" onClick={markAllAsRead} title="Marcar todas como lidas">
              <FiCheck />
            </button>
          )}
        </div>

        <div className="omex-notifications-list">
          {loading ? (
            <div className="omex-notifications-empty">Carregando...</div>
          ) : error ? (
            <button className="omex-notifications-retry" type="button" onClick={loadNotifications}>
              <FiRefreshCw />
              Atualizar notificações
            </button>
          ) : comunicacoes.length ? (
            comunicacoes.map((comunicacao) => (
              <button
                className={`omex-notification-item ${comunicacao.lida ? "read" : "unread"}`}
                key={comunicacao.id}
                type="button"
                onClick={() => markAsRead(comunicacao)}
              >
                <span className="omex-notification-icon">{getNotificationIcon(comunicacao.tipo)}</span>
                <span className="omex-notification-copy">
                  <strong>{comunicacao.titulo}</strong>
                  <small>{comunicacao.mensagem}</small>
                </span>
                <time>{formatNotificationDate(comunicacao.atualizadoEm)}</time>
              </button>
            ))
          ) : (
            <div className="omex-notifications-empty">Nenhuma notificação publicada.</div>
          )}
        </div>

        <button className="omex-notifications-footer" type="button" onClick={() => navigate("/relatorios")}>
          Ver relatórios
        </button>
      </Dropdown.Menu>
    </Dropdown>
  );
};
